/**
 *
 * @param {HTMLCanvasElement} canvas
 * @param {String} [mimeType='image/png']
 * @param {Number} [quality=1] 0..1
 *
 * @return {Promise<Blob>}
 */
export function toBlob(canvas, mimeType = "image/png", quality = 1) {
  return new Promise(resolve => {
    canvas.toBlob(
      blob => {
        resolve(blob);
      },
      mimeType,
      quality
    );
  });
}

/**
 *
 * @param {HTMLCanvasElement} canvas
 * @param {{ x: Number, y: Number }} from
 * @param {{ x: Number, y: Number }} to
 * @param {Object} [options] See more Line styles in https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D
 * @param {String} [options.strokeStyle='#000']
 * @param {Number} [options.lineWidth=1]
 */
export function drawLine(canvas, from, to, options = {}) {
  const ctx = canvas.getContext("2d");

  const ctxOptions = {
    strokeStyle: "#000",
    lineWidth: 1,
    ...options
  };

  ctx.save();

  for (const key of Object.keys(ctxOptions)) {
    ctx[key] = ctxOptions[key];
  }

  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x, to.y);
  ctx.stroke();
  ctx.restore();

  return canvas;
}

/**
 * Draw text
 *
 * @param {HTMLCanvasElement} canvas
 * @param {String} text
 * @param {{ x: Number, y: Number }} position
 * @param {Object} [options] See more Text styles in https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D
 * @param {Number} [options.rotate=0]
 * @param {Number} [options.lineHeight=20]
 * @param {String} [options.font='14pt Arial']
 * @param {String} [options.strokeStyle='#000']
 * @param {String} [options.fillStyle='#000']
 * @param {String} [options.textAlign='center']
 * @param {String} [options.textBaseline='alphabetic']
 */
export function drawText(canvas, text, position, options = {}) {
  const ctx = canvas.getContext("2d");

  const ctxOptions = {
    rotate: 0,
    lineHeight: 20,
    font: "14pt Arial",
    strokeStyle: "#000",
    fillStyle: "#000",
    textAlign: "center",
    ...options
  };

  ctx.save();
  ctx.translate(position.x, position.y);
  ctx.rotate((ctxOptions.rotate * Math.PI) / 180);

  for (const key of Object.keys(ctxOptions)) {
    if (key !== "rotate" && key !== "lineHeight") ctx[key] = ctxOptions[key];
  }

  ctx.fillText(text, 0, ctxOptions.lineHeight / 2);
  ctx.restore();

  return canvas;
}

/**
 * Draw wrap text
 *
 * @param {HTMLCanvasElement} canvas
 * @param {String} text
 * @param {Number} x
 * @param {Number} y
 * @param {Number} maxWidth
 * @param {Object} [options]
 * @param {Number} [options.lineSpace] Default: 1.25
 * @param {String} [options.font='14pt Arial'] Default: '14pt Arial'
 * @param {String} [options.strokeStyle='#000'] Default: '#000'
 * @param {String} [options.fillStyle='#000'] Default: '#000'
 * @param {String} [options.textAlign='start'] Default: 'start'. Values: "left" || "right" || "center" || "start" || "end"
 * @param {String} [options.textBaseline='top'] Default: 'top'. Values: "top" || "hanging" || "middle" || "alphabetic" || "ideographic" || "bottom"
 */
export function drawWrapText(canvas, text, x, y, maxWidth, options = {}) {
  const ctx = canvas.getContext("2d");

  const ctxOptions = {
    font: "14pt Arial",
    strokeStyle: "#000",
    fillStyle: "#000",
    textAlign: "start",
    textBaseline: "top",
    ...options
  };

  const lineSpace = options.lineSpace || 1.25;

  ctx.save();

  for (const key of Object.keys(ctxOptions)) {
    ctx[key] = ctxOptions[key];
  }

  const words = text.split(" ");
  let line = "";
  const textMetrics = ctx.measureText(text);
  const heightOfALine =
    (textMetrics.actualBoundingBoxAscent +
      textMetrics.actualBoundingBoxDescent) *
    lineSpace;

  for (let n = 0, len = words.length; n < len; n++) {
    const testLine = line + words[n] + " ";
    const metrics = ctx.measureText(testLine);
    const testWidth = metrics.width;
    if (testWidth > maxWidth && n > 0) {
      ctx.fillText(line, x, y);
      line = words[n] + " ";
      y += heightOfALine;
    } else {
      line = testLine;
    }
  }

  ctx.fillText(line, x, y);

  ctx.restore();

  return canvas;
}

/**
 * Calculate wrap text height
 *
 * @param {String} font
 * @param {String} text
 * @param {Number} x
 * @param {Number} y
 * @param {Number} maxWidth
 * @param {Number} [lineSpace] Default: 1.25
 */
export function measureWrapTextHeight(font, text, maxWidth, lineSpace = 1.25) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  ctx.textAlign = "top";
  ctx.font = font;

  const words = text.split(" ");
  let line = "";
  const textMetrics = ctx.measureText(text);
  const heightOfALine =
    (textMetrics.actualBoundingBoxAscent +
      textMetrics.actualBoundingBoxDescent) *
    lineSpace;

  let y = 0;

  for (let n = 0, len = words.length; n < len; n++) {
    const testLine = line + words[n] + " ";
    const metrics = ctx.measureText(testLine);
    const testWidth = metrics.width;
    if (testWidth > maxWidth && n > 0) {
      line = words[n] + " ";
      y += heightOfALine;
    } else {
      line = testLine;
    }
  }

  return y + heightOfALine;
}

/**
 *
 * @param {String} text
 * @param {String} [font] Default: 10px sans-serif
 * @param {Number} [lineSpace] Default: 1.25
 */
export function measureTextSize(
  text,
  font = "10px sans-serif",
  lineSpace = 1.25
) {
  const c = document.createElement("canvas");
  const ctx = c.getContext("2d");
  ctx.font = font;

  const textMetrics = ctx.measureText(text);

  return {
    width: textMetrics.width,
    height:
      (textMetrics.actualBoundingBoxAscent +
        textMetrics.actualBoundingBoxDescent) *
      lineSpace
  };
}

/**
 * Draw image into canvas
 * @param {Canvas} canvas canvas to draw image into
 * @param {CanvasImageSource|HTMLImageElement|SVGImageElement|HTMLVideoElement|HTMLCanvasElement|ImageBitmap|OffscreenCanvas} image image need to draw into canvas
 * @param {Object} [options]
 * @param {Number} [options.width] default: image.width
 * @param {Number} [options.height] default: image.height
 * @param {Number} [options.left] used when options.right is not provided
 * @param {Number} [options.right] used when options.left is not provided
 * @param {Number} [options.top] used when options.bottom is not provided
 * @param {Number} [options.bottom] used when options.top is not provided
 */
export function drawImage(canvas, image, options = {}) {
  const w = isNaN(options.width) ? image.width : options.width;
  const h = isNaN(options.height) ? image.height : options.height;
  const x = isNaN(options.left)
    ? canvas.width - options.right - w
    : options.left;
  const y = isNaN(options.top)
    ? canvas.height - options.bottom - h
    : options.top;

  const ctx = canvas.getContext("2d");

  ctx.drawImage(image, x, y);

  return canvas;
}

/**
 *
 * @param {HTMLCanvasElement} canvas
 * @param {Number} x
 * @param {Number} y
 * @param {Number} width
 * @param {Number} height
 * @param {Object} [options] See more Line styles in https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D
 * @param {String} [options.strokeStyle='#000']
 * @param {String} [options.fillStyle='#fff']
 * @param {Number} [options.lineWidth=1]
 */
export function drawRect(canvas, x, y, width, height, options = {}) {
  const ctx = canvas.getContext("2d");

  const ctxOptions = {
    strokeStyle: "#000",
    fillStyle: "#fff",
    lineWidth: 1,
    ...options
  };

  ctx.save();

  for (const key of Object.keys(ctxOptions)) {
    ctx[key] = ctxOptions[key];
  }

  ctx.fillRect(x, y, width, height);
  ctx.strokeRect(x, y, width, height);

  ctx.restore();

  return canvas;
}

/**
 *
 * @param {HTMLCanvasElement} canvas
 * @param {Number} x
 * @param {Number} y
 * @param {Number} radius
 * @param {Object} [options] See more Line styles in https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D
 * @param {String} [options.strokeStyle='#fff']
 * @param {String} [options.fillStyle='#000']
 * @param {Number} [options.lineWidth=1]
 */
export function drawCircle(canvas, x, y, radius, options) {
  const ctx = canvas.getContext("2d");

  ctx.save();

  // Style
  const ctxOptions = {
    strokeStyle: "#fff",
    fillStyle: "#000",
    lineWidth: 1,
    ...options
  };

  for (const key of Object.keys(ctxOptions)) {
    ctx[key] = ctxOptions[key];
  }

  // Active arc mode
  ctx.arc(x, y, radius, 0, 2 * Math.PI);

  // Draw Fill Circle
  ctx.fill();

  // Draw stroke
  ctx.stroke();

  ctx.restore();

  return canvas;
}

/**
 *
 * @param {HTMLCanvasElement} canvas
 * @param {Object} [options]
 * @param {String} [options.color='white']
 */
export function clearBackground(canvas, options = {}) {
  const ctx = canvas.getContext("2d");
  ctx.save();
  ctx.fillStyle = options.color || "white";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.restore();
}

/**
 *
 * @param {HTMLCanvasElement} canvas
 * @param {String} type MIME type
 * @param {Number} quality
 */
export function toBase64(canvas, type = "image/png", quality = 0.92) {
  return canvas.toDataURL(type, quality);
}

export function createCanvas(width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  return canvas;
}

export class CanvasBuilder {
  constructor(width, height) {
    this.canvas = createCanvas(width, height);
  }

  // Start builder
  static create(width, height) {
    return new CanvasBuilder(width, height);
  }

  // Modifier
  /**
   *
   * @param {{ x: Number, y: Number }} from
   * @param {{ x: Number, y: Number }} to
   * @param {Object} [options] See more Line styles in https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D
   * @param {String} [options.strokeStyle='#000']
   * @param {Number} [options.lineWidth=1]
   */
  drawLine(from, to, options = {}) {
    drawLine(this.canvas, from, to, options);

    return this;
  }

  /**
   * Draw text
   *
   * @param {String} text
   * @param {{ x: Number, y: Number }} position
   * @param {Object} [options] See more Text styles in https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D
   * @param {Number} [options.rotate=0]
   * @param {Number} [options.lineHeight=20]
   * @param {String} [options.font='14pt Arial']
   * @param {String} [options.strokeStyle='#000']
   * @param {String} [options.fillStyle='#000']
   * @param {String} [options.textAlign='center'] "left" || "right" || "center" || "start" || "end"
   * @param {String} [options.textBaseline='alphabetic'] "top" || "hanging" || "middle" || "alphabetic" || "ideographic" || "bottom"
   */
  drawText(text, position, options = {}) {
    drawText(this.canvas, text, position, options);

    return this;
  }

  /**
   * Draw wrap text
   * @param {String} text
   * @param {Number} x
   * @param {Number} y
   * @param {Number} maxWidth
   * @param {Number} [lineSpace] Default: 1.25
   */
  drawWrapText(text, x, y, maxWidth, options = {}) {
    drawWrapText(this.canvas, text, x, y, maxWidth, options);

    return this;
  }

  /**
   * Draw image into canvas
   * @param {Canvas} canvas canvas to draw image into
   * @param {CanvasImageSource|HTMLImageElement|SVGImageElement|HTMLVideoElement|HTMLCanvasElement|ImageBitmap|OffscreenCanvas} image image need to draw into canvas
   * @param {Object} [options]
   * @param {Number} [options.width] default: image.width
   * @param {Number} [options.height] default: image.height
   * @param {Number} [options.left] use options.right if not provided
   * @param {Number} [options.right] use options.left if not provided
   * @param {Number} [options.top] use options.bottom if not provided
   * @param {Number} [options.bottom] use options.top if not provided
   */
  drawImage(image, options = {}) {
    drawImage(this.canvas, image, options);

    return this;
  }

  /**
   *
   * @param {Number} x
   * @param {Number} y
   * @param {Number} width
   * @param {Number} height
   * @param {Object} [options] See more Line styles in https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D
   * @param {String} [options.strokeStyle='#000']
   * @param {String} [options.fillStyle='#fff']
   * @param {Number} [options.lineWidth=1]
   */
  drawRect(x, y, width, height, options = {}) {
    drawRect(this.canvas, x, y, width, height, options);

    return this;
  }

  /**
   *
   * @param {Number} x
   * @param {Number} y
   * @param {Number} radius
   * @param {Object} [options] See more Line styles in https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D
   * @param {String} [options.strokeStyle='#fff']
   * @param {String} [options.fillStyle='#000']
   * @param {Number} [options.lineWidth=1]
   */
  drawCircle(x, y, radius, options) {
    drawCircle(this.canvas, x, y, radius, options);

    return this;
  }

  /**
   *
   * @param {Object} [options]
   * @param {String} [options.color='white']
   */
  clearBackground(options = {}) {
    clearBackground(this.canvas, options);

    return this;
  }

  // Output
  toBlob(mimeType = "image/png", quality = 1) {
    return toBlob(this.canvas, mimeType, quality);
  }

  toBase64(type = "image/png", quality = 0.92) {
    return toBase64(this.canvas, type, quality);
  }

  toCanvas() {
    return this.canvas;
  }
}
